/*
  These helpers use Typescript to define the interface
  and automatically JSON parse the value
  
  Inspired by https://www.npmjs.com/package/typed-local-store

  Extend the interface like a so:
  declare global {
    interface LocalStorage {
      exampleKey: exampleType
    }
    interface SessionStorage {
      exampleKey: exampleType
    }
  }
 */

declare global {
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface LocalStorage {
    lastVisitedPages: {
      date: string;
      image: string;
      title: string;
      url: string;
    }[];
  }

  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface SessionStorage {
    solvariSession: string;
  }
}

class MemoryStorage implements Storage {
  private readonly storage: Map<string, string>;

  constructor() {
    this.storage = new Map<string, string>();
  }

  public get length() {
    return [...this.storage.keys()].length;
  }

  public key(index: number) {
    return [...this.storage.keys()][index] ?? null;
  }

  public getItem(key: string) {
    return this.storage.get(key) || null;
  }

  public setItem(key: string, value: string) {
    this.storage.set(key, value);
  }

  public removeItem(key: string) {
    this.storage.delete(key);
  }

  public clear() {
    this.storage.clear();
  }
}

class TypedStorage<StorageType> {
  private readonly storage: Storage;

  constructor(storage: "localStorage" | "sessionStorage" = "localStorage") {
    try {
      // oxlint-disable-next-line typescript/no-unnecessary-condition -- Can sometimes still be null in some browsers
      if (window[storage]) {
        this.storage = window[storage];
      } else {
        this.storage = new MemoryStorage();
      }
    } catch (_error) {
      this.storage = new MemoryStorage();
    }
  }

  public get length() {
    return this.storage.length;
  }

  public key(index: number) {
    return this.storage.key(index) as keyof StorageType | null;
  }

  /**
   * Retrieves a typed value from storage with automatic JSON parsing.
   *
   * @param key - The storage key to retrieve
   * @returns The typed value, or null if not found
   * @example const pages = sLocalStorage.getItem('lastVisitedPages');
   */
  public getItem<Key extends string & keyof StorageType>(key: Key) {
    const item = this.storage.getItem(key);

    if (!item) {
      return null;
    }

    try {
      return JSON.parse(item) as StorageType[Key];
    } catch (_error) {
      return item as StorageType[Key];
    }
  }

  /**
   * Stores a typed value in storage with automatic JSON stringification.
   *
   * @param key - The storage key to store under
   * @param value - The typed value to store
   * @example sLocalStorage.setItem('lastVisitedPages', [{ date: '2025-01-13', ... }]);
   */
  public setItem<Key extends string & keyof StorageType>(
    key: Key,
    value: StorageType[Key],
  ) {
    const stringValue =
      typeof value !== "string" ? JSON.stringify(value) : value;
    this.storage.setItem(key, stringValue);
  }

  /**
   * Updates an existing storage value using an updater function.
   * Retrieves current value, passes to updater, and stores returned value.
   *
   * @param key - The storage key to update
   * @param update - Function that receives old value and returns new value
   * @example sLocalStorage.updateItem('lastVisitedPages', (old) => [...(old || []), newPage]);
   */
  public updateItem<Key extends string & keyof StorageType>(
    key: Key,
    update: (oldValue: StorageType[Key] | null) => StorageType[Key],
  ) {
    this.setItem(key, update(this.getItem(key)));
  }

  /**
   * Removes a value from storage.
   *
   * @param {keyof StorageType} key - The key to remove
   *
   * @example
   * ```typescript
   * sLocalStorage.removeItem('lastVisitedPages');
   * ```
   */
  public removeItem(key: keyof StorageType) {
    this.storage.removeItem(key.toString());
  }

  /**
   * Clears all values from storage.
   *
   * @example
   * ```typescript
   * sLocalStorage.clear();
   * ```
   */
  public clear() {
    this.storage.clear();
  }
}

/**
 * Type-safe localStorage wrapper with automatic JSON serialization.
 * Provides typed interface with auto JSON parsing. Fallback to in-memory if unavailable.
 *
 * @example
 * const settings = sLocalStorage.getItem('userSettings');
 * sLocalStorage.setItem('userSettings', { theme: 'dark', notifications: true });
 * sLocalStorage.updateItem('userSettings', (old) => ({ ...old, theme: 'light' }));
 * @remarks Extend global LocalStorage interface to add type safety for your keys.
 */
const sLocalStorage = new TypedStorage<LocalStorage>("localStorage");

/**
 * Type-safe sessionStorage wrapper with automatic JSON serialization.
 * Provides typed interface with auto JSON parsing. Data persists per tab session only.
 *
 * @example
 * sSessionStorage.setItem('wizardState', { step: 2, data: { name: 'John' } });
 * const state = sSessionStorage.getItem('wizardState');
 * @remarks Extend global SessionStorage interface to add type safety for your keys.
 */
const sSessionStorage = new TypedStorage<SessionStorage>("sessionStorage");

export { sLocalStorage, sSessionStorage };
